import { extendVariants, Chip as NextUIChip } from "@heroui/react";
import clsx from "clsx";

const ExtendedChip = extendVariants(NextUIChip, {
  compoundVariants: [
    // isOneChar / size
    {
      class: ["w-5 h-5 min-w-5 min-h-5"],
      hasEndContent: false,
      hasStartContent: false,
      isOneChar: true,
      size: "sm",
    },
    {
      class: ["w-6 h-6 min-w-6 min-h-6"],
      hasEndContent: false,
      hasStartContent: false,
      isOneChar: true,
      size: "md",
    },
    {
      class: ["w-7 h-7 min-w-7 min-h-7"],
      hasEndContent: false,
      hasStartContent: false,
      isOneChar: true,
      size: "lg",
    },
    // isOneChar / isCloseable
    {
      class: ["px-0 justify-center", "px-0 flex-none"],
      hasEndContent: false,
      hasStartContent: false,
      isCloseable: false,
      isOneChar: true,
    },
    {
      class: ["w-auto"],
      hasEndContent: false,
      hasStartContent: false,
      isCloseable: true,
      isOneChar: true,
    },
    // isOneChar / dot
    {
      class: ["w-auto h-7 px-1 items-center", "px-2"],
      isOneChar: true,
      variant: "dot",
    },
    // hasStartContent / size
    {
      class: ["pl-0.5"],
      hasStartContent: true,
      size: "sm",
    },
    {
      class: ["pl-1"],
      hasStartContent: true,
      size: ["md", "lg"],
    },
    // hasEndContent / size
    {
      class: ["pr-0.5"],
      hasEndContent: true,
      size: "sm",
    },
    {
      class: ["pr-1"],
      hasEndContent: true,
      size: ["md", "lg"],
    },
  ],
  defaultVariants: {
    color: "default",
    isDisabled: false,
    radius: "full",
    size: "md",
    variant: "solid",
  },
  slots: {
    avatar: ["shrink-0", "h-4"],
    base: [
      "relative",
      "max-w-fit",
      "min-w-min",
      "inline-flex",
      "items-center",
      "justify-between",
      "box-border",
      "whitespace-nowrap",
    ],
    content: "flex-1 font-normal",
    dot: ["w-2", "h-2", "ml-1", "rounded-full"],
  },
  variants: {
    color: {
      accent: {},
      black: {},
      gray: {},
      white: {},
    },
    hasEndContent: { false: {}, true: {} },
    hasStartContent: { false: {}, true: {} },
    isCloseable: { false: {}, true: {} },
    isDisabled: { false: {}, true: {} },
    isOneChar: { false: {}, true: {} },
    size: {
      lg: {
        base: ["px-3", "py-2", "h-9"],
        content: ["px-2 text-small"],
      },
      md: {
        base: ["px-3", "py-1.5", "h-8"],
        content: ["px-2 text-small"],
      },
      sm: {
        base: ["px-2", "py-1", "h-7"],
        content: ["px-1 text-body-sm"],
      },
    },
  },
});

type ExtendedChipProps = React.ComponentProps<typeof ExtendedChip>;

type Variant = ExtendedChipProps["variant"];
type Color = ExtendedChipProps["color"];

export type ChipProps = Omit<ExtendedChipProps, "variant" | "color" | "id"> & {
  color?: Color;
  id: string;
  variant?: Variant;
  ref?: React.Ref<HTMLDivElement>;
};

type VariantColorMap = Partial<Record<NonNullable<Variant>, string>>;

const colorStyles: Record<string, VariantColorMap> = {
  accent: {
    bordered: "border-accent-dark text-accent-dark",
    dot: "border-white border-gray-500 text-base-900",
    faded: "bg-gray-50 border-gray-500 text-accent-dark",
    flat: "bg-accent-light text-purple-800",
    solid: "bg-accent-dark text-gray-900",
  },
  black: {
    bordered: "border-black text-base-900",
    dot: "border-gray-500 text-gray-900",
    faded: "bg-gray-50 border-gray-500 text-base-900",
    flat: "bg-black text-white",
    solid: "bg-black text-gray-900",
  },
  danger: {
    dot: "border-default",
    faded: "bg-gray-50 border-gray-500 text-danger-dark",
    flat: "bg-danger-light text-red-900",
  },
  default: {
    faded: "border-gray-500",
  },
  gray: {
    bordered: "border-gray-500 text-gray-900",
    dot: "border-gray-500 text-gray-900",
    faded: "bg-gray-50 border-gray-500 text-gray-600",
    flat: "bg-gray-400",
    solid: "bg-gray-400 text-gray-900",
  },
  primary: {
    dot: "border-gray-500 text-gray-900",
    faded: "bg-gray-50 border-gray-500",
    flat: "bg-blue-100 text-primary-dark",
  },
  secondary: {
    dot: "border-white text-white",
    faded: "bg-gray-900 border-gray-500 text-secondary-dark",
    flat: "bg-secondary-light text-yellow-900",
  },
  success: {
    dot: "border-gray-500 text-green-700",
    faded: "bg-gray-50 border-gray-500",
    flat: "bg-success-light text-green-700",
  },
  warning: {
    bordered: "border-warning-dark text-warning-dark",
    dot: "border-gray-500",
    faded: "bg-gray-50 border-gray-500 text-warning-dark",
    flat: "bg-warning-light text-orange-800",
  },
  white: {
    bordered: "border-white text-white",
    dot: "border-white text-white",
    faded: "bg-gray-900 border-gray-500 text-white",
    flat: "bg-white",
    solid: "bg-white text-gray-900",
  },
};

const dotColorClasses: Partial<Record<NonNullable<Color>, string>> = {
  accent: "bg-accent-dark",
  black: "bg-black",
  gray: "bg-gray-500",
  white: "bg-white",
};

export function Chip({ ref, ...props }: ChipProps) {
  const dotClass =
    props.color && props.variant === "dot"
      ? dotColorClasses[props.color]
      : undefined;

  const colorClass =
    props.color && props.variant
      ? colorStyles[props.color]?.[props.variant]
      : undefined;

  const contentClass = props.variant === "dot" ? "pr-0!" : undefined;

  return (
    <ExtendedChip
      {...props}
      ref={ref}
      classNames={{
        base: colorClass,
        closeButton: clsx("opacity-100 ring-transparent", {
          "focus:ring-offset-primary": props.color !== "primary",
          "focus:ring-offset-white": props.color === "primary",
        }),
        content: contentClass,
        dot: dotClass,
      }}
      data-testid={`chip-${props.id}`}
    />
  );
}

Chip.displayName = "RecareUI.Chip";
